import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AlertTriangle, ArrowUpDown, ChevronUp, ChevronDown, Search } from 'lucide-react';
import { formatCurrency } from '../../lib/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fmtDate = (d) => { if (!d) return '-'; const dt = new Date(d); return `${String(dt.getUTCMonth()+1).padStart(2,'0')}/${String(dt.getUTCDate()).padStart(2,'0')}/${dt.getUTCFullYear()}`; };

const SortIcon = ({ field, sortField, sortDir }) => {
  if (field !== sortField) return <ArrowUpDown className="h-3 w-3 opacity-30" />;
  return sortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />;
};

const PortfolioDefaultDealsTable = ({ getAuthHeaders }) => {
  const navigate = useNavigate();
  const [data, setData] = useState({ deals: [], stats: { active: 0, settled: 0, written_off: 0, avg_recovery: 0 } });
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('date_defaulted');
  const [sortDir, setSortDir] = useState('desc');

  // Filters
  const [lawFirmFilter, setLawFirmFilter] = useState('all');
  const [caseStatusFilter, setCaseStatusFilter] = useState('all');
  const [writeOffFilter, setWriteOffFilter] = useState('all');
  const [searchText, setSearchText] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/default-recovery/defaulted-deals`, { headers: getAuthHeaders() });
      setData(res.data);
    } catch (err) {
      console.error('Failed to load defaulted deals:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Unique law firms for filter
  const lawFirms = useMemo(() => {
    const firms = [...new Set(data.deals.map(d => d.law_firm).filter(Boolean))];
    return firms.sort();
  }, [data.deals]);

  // Unique case statuses for filter
  const caseStatuses = useMemo(() => {
    return [...new Set(data.deals.map(d => d.case_status))].sort();
  }, [data.deals]);

  // Filtered and sorted deals
  const filteredDeals = useMemo(() => {
    let result = [...data.deals];

    if (searchText) {
      const q = searchText.toLowerCase();
      result = result.filter(d =>
        d.deal_id?.toString().includes(q) ||
        d.deal_name?.toLowerCase().includes(q) ||
        d.law_firm?.toLowerCase().includes(q)
      );
    }
    if (lawFirmFilter !== 'all') result = result.filter(d => d.law_firm === lawFirmFilter);
    if (caseStatusFilter !== 'all') result = result.filter(d => d.case_status === caseStatusFilter);
    if (writeOffFilter === 'yes') result = result.filter(d => d.is_written_off);
    if (writeOffFilter === 'no') result = result.filter(d => !d.is_written_off);

    result.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (typeof aVal === 'string') { aVal = aVal.toLowerCase(); bVal = (bVal || '').toLowerCase(); }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [data.deals, searchText, lawFirmFilter, caseStatusFilter, writeOffFilter, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const handleRowClick = (deal) => {
    navigate(`/deals/${deal.deal_id}?tab=default-recovery`);
  };

  const handleExportCSV = async () => {
    try {
      const res = await axios.get(`${API}/default-recovery/export-defaulted-deals-csv`, {
        headers: getAuthHeaders(), responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'defaulted_deals_export.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV export failed:', err);
    }
  };

  if (loading) return null;
  if (data.deals.length === 0) return null;

  const { stats } = data;

  return (
    <Card className="mb-6" data-testid="portfolio-defaulted-deals-table">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-500" />
            Defaulted Deals ({filteredDeals.length})
          </CardTitle>
          <Button variant="outline" size="sm" onClick={handleExportCSV} data-testid="export-defaulted-csv">
            Export CSV
          </Button>
        </div>

        {/* Quick Stats */}
        <div className="flex gap-4 mt-2 text-sm flex-wrap">
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-2 h-2 rounded-full bg-red-500"></span>
            <span className="text-muted-foreground">Active:</span>
            <span className="font-semibold" data-testid="stat-active-cases">{stats.active}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-2 h-2 rounded-full bg-green-500"></span>
            <span className="text-muted-foreground">Settled:</span>
            <span className="font-semibold" data-testid="stat-settled-cases">{stats.settled}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-2 h-2 rounded-full bg-yellow-500"></span>
            <span className="text-muted-foreground">Written Off:</span>
            <span className="font-semibold" data-testid="stat-written-off-cases">{stats.written_off}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground">Avg Recovery:</span>
            <span className="font-semibold text-blue-600" data-testid="stat-avg-recovery">{stats.avg_recovery}%</span>
          </div>
        </div>

        {/* Filters Row */}
        <div className="flex gap-2 mt-3 flex-wrap items-end">
          <div className="relative flex-1 min-w-[180px] max-w-[280px]">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search deal ID, name, firm..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              className="pl-8 h-9 text-sm"
              data-testid="default-deals-search"
            />
          </div>
          <Select value={lawFirmFilter} onValueChange={setLawFirmFilter}>
            <SelectTrigger className="h-9 w-[160px] text-sm" data-testid="filter-law-firm">
              <SelectValue placeholder="Law Firm" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Firms</SelectItem>
              {lawFirms.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={caseStatusFilter} onValueChange={setCaseStatusFilter}>
            <SelectTrigger className="h-9 w-[140px] text-sm" data-testid="filter-case-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {caseStatuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={writeOffFilter} onValueChange={setWriteOffFilter}>
            <SelectTrigger className="h-9 w-[140px] text-sm" data-testid="filter-write-off">
              <SelectValue placeholder="Write-Off" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="yes">Written Off</SelectItem>
              <SelectItem value="no">Not Written Off</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-muted-foreground text-left">
                <Th field="deal_id" label="Deal" {...{sortField, sortDir, handleSort}} />
                <Th field="deal_name" label="Merchant" {...{sortField, sortDir, handleSort}} />
                <Th field="date_defaulted" label="Date Defaulted" {...{sortField, sortDir, handleSort}} />
                <th className="py-2 px-3 font-medium text-xs">Law Firm</th>
                <Th field="default_balance" label="Default Bal" {...{sortField, sortDir, handleSort}} />
                <Th field="current_balance" label="Current Bal" {...{sortField, sortDir, handleSort}} />
                <Th field="total_gross_collected" label="Gross Collected" {...{sortField, sortDir, handleSort}} />
                <th className="py-2 px-3 font-medium text-xs">Write-Off</th>
                <Th field="recovery_pct" label="Recovery %" {...{sortField, sortDir, handleSort}} />
                <th className="py-2 px-3 font-medium text-xs">Status</th>
                <th className="py-2 px-3 font-medium text-xs">Last Collection</th>
                <th className="py-2 px-3 font-medium text-xs">Flags</th>
              </tr>
            </thead>
            <tbody>
              {filteredDeals.map(d => (
                <tr
                  key={d.deal_uuid}
                  onClick={() => handleRowClick(d)}
                  className="border-b last:border-0 hover:bg-muted/30 cursor-pointer transition-colors"
                  data-testid={`default-deal-row-${d.deal_id}`}
                >
                  <td className="py-2.5 px-3 font-medium">#{d.deal_id}</td>
                  <td className="py-2.5 px-3 max-w-[160px] truncate">{d.deal_name || '-'}</td>
                  <td className="py-2.5 px-3">{fmtDate(d.date_defaulted)}</td>
                  <td className="py-2.5 px-3 text-muted-foreground max-w-[120px] truncate">{d.law_firm || '-'}</td>
                  <td className="py-2.5 px-3 font-medium text-red-600">{formatCurrency(d.default_balance)}</td>
                  <td className="py-2.5 px-3">{formatCurrency(d.current_balance)}</td>
                  <td className="py-2.5 px-3 font-medium text-green-600">
                    {d.total_gross_collected > 0 ? formatCurrency(d.total_gross_collected) : '-'}
                    {d.total_gross_collected > 0 && (
                      <div className="text-[10px] font-normal text-muted-foreground whitespace-nowrap">
                        lawyer {formatCurrency(d.lawyer_collected || 0)} · in-house {formatCurrency(d.in_house_collected || 0)}
                      </div>
                    )}
                  </td>
                  <td className="py-2.5 px-3">{d.total_write_off > 0 ? formatCurrency(d.total_write_off) : '-'}</td>
                  <td className="py-2.5 px-3">
                    <span className={`font-semibold ${d.recovery_pct >= 75 ? 'text-green-600' : d.recovery_pct >= 25 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {d.recovery_pct}%
                    </span>
                  </td>
                  <td className="py-2.5 px-3">
                    <Badge variant={
                      d.case_status === 'Active' || d.case_status === 'Open' ? 'default' :
                      d.case_status === 'Settled' ? 'secondary' :
                      d.case_status === 'Closed' ? 'outline' : 'outline'
                    } className="text-xs">
                      {d.case_status}
                    </Badge>
                    {d.recovery_status && (
                      <div className="text-[10px] text-muted-foreground mt-0.5 whitespace-nowrap">
                        {d.recovery_status}{d.proposed_settlement_amount ? ` · ${formatCurrency(d.proposed_settlement_amount)}` : ''}
                      </div>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-muted-foreground">{fmtDate(d.last_collection_date)}</td>
                  <td className="py-2.5 px-3 space-x-1">
                    {d.over_collection && (
                      <Badge variant="outline" className="text-xs border-blue-300 text-blue-700 bg-blue-50" data-testid={`extras-badge-${d.deal_id}`}>EXTRAS</Badge>
                    )}
                    {d.is_written_off && (
                      <Badge variant="outline" className="text-xs border-yellow-300 text-yellow-700 bg-yellow-50" data-testid={`writeoff-badge-${d.deal_id}`}>Written Off</Badge>
                    )}
                  </td>
                </tr>
              ))}
              {filteredDeals.length === 0 && (
                <tr><td colSpan={12} className="py-8 text-center text-muted-foreground">No matching defaulted deals</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

const Th = ({ field, label, sortField, sortDir, handleSort }) => (
  <th
    className="py-2 px-3 font-medium text-xs cursor-pointer select-none hover:text-foreground transition-colors"
    onClick={() => handleSort(field)}
  >
    <span className="flex items-center gap-1">
      {label}
      <SortIcon field={field} sortField={sortField} sortDir={sortDir} />
    </span>
  </th>
);

export default PortfolioDefaultDealsTable;
